import * as React from "react";
import { CalendarIcon } from "lucide-react";
import { format, isAfter, isBefore, isValid, parse } from "date-fns";
import { DialogTitle } from "@radix-ui/react-dialog";
import { useDayPicker, } from "react-day-picker";
import { cn } from "../../lib/utils";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Calendar } from "../../components/ui/calendar";
import { Button, buttonVariants } from "../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogTrigger, } from "../../components/ui/dialog";
const PickerDialog = ({ start, end, onChange, onClose, }) => {
    const [startInputValue, setStartInputValue] = React.useState("");
    const [endInputValue, setEndInputValue] = React.useState("");
    const [tempStartDate, setTempStartDate] = React.useState(start);
    const [tempEndDate, setTempEndDate] = React.useState(end);
    const [month, setMonth] = React.useState(start || new Date());
    const disabled = !tempStartDate || (tempEndDate && tempEndDate < tempStartDate);
    const CaptionLabel = React.useCallback((props) => {
        const { locale, formatters: { formatCaption }, } = useDayPicker();
        return (React.createElement("button", { className: cn(buttonVariants({ variant: "outline" }), "h-7 py-0 px-3 bg-transparent opacity-50 hover:opacity-100"), "aria-live": "polite", onClick: () => {
                setMonth((month) => {
                    const start = new Date(month);
                    start.setDate(1);
                    const end = new Date(start);
                    end.setMonth(month.getMonth() + 1);
                    end.setDate(0);
                    setTempStartDate(start);
                    setTempEndDate(end);
                    return month;
                });
            }, role: "presentation", id: props.id }, formatCaption(props.displayMonth, { locale })));
    }, []);
    const handleWeekSelection = (_, dates) => {
        const start = dates[0];
        const end = dates[dates.length - 1];
        setTempStartDate(start);
        setTempEndDate(end);
    };
    React.useEffect(() => {
        setStartInputValue(tempStartDate ? format(tempStartDate, "dd.MM.yyyy") : "");
    }, [tempStartDate]);
    React.useEffect(() => {
        setEndInputValue(tempEndDate ? format(tempEndDate, "dd.MM.yyyy") : "");
    }, [tempEndDate]);
    const handleCurrentDay = (day) => {
        if (tempStartDate && !tempEndDate && +day === +tempStartDate) {
            setTempEndDate(day);
        }
    };
    // Handle calendar date selection
    const handleSelect = (day) => {
        if (!tempStartDate || (tempStartDate && tempEndDate)) {
            // Start new selection
            setTempStartDate(day);
            setTempEndDate(undefined);
        }
        else if (!day) {
            // Skip non provided day, e.g., if it's same as start
        }
        else if (isBefore(day, tempStartDate)) {
            // Complete the selection in reverse order
            setTempStartDate(day);
            setTempEndDate(tempStartDate);
        }
        else {
            // Complete the selection in normal order
            setTempEndDate(day);
        }
    };
    // Handle start date input change
    const handleStartInputChange = (e) => {
        const value = e.target.value;
        setStartInputValue(value);
        if (value.length === 10) {
            const parsedDate = parse(value, "dd.MM.yyyy", new Date());
            if (isValid(parsedDate)) {
                setTempStartDate(parsedDate);
                if (tempEndDate && isAfter(parsedDate, tempEndDate)) {
                    setTempEndDate(undefined);
                }
                setMonth(parsedDate);
            }
        }
    };
    // Handle end date input change
    const handleEndInputChange = (e) => {
        const value = e.target.value;
        setEndInputValue(value);
        if (value.length === 10) {
            const parsedDate = parse(value, "dd.MM.yyyy", new Date());
            if (isValid(parsedDate)) {
                setTempEndDate(parsedDate);
                if (tempStartDate && isBefore(parsedDate, tempStartDate)) {
                    setTempStartDate(parsedDate);
                }
            }
        }
    };
    // Handle save button click
    const handleSave = () => {
        const start = tempStartDate;
        const s = new Date(start);
        const e = new Date(tempEndDate || start);
        e.setDate(e.getDate() + 1);
        e.setMilliseconds(e.getMilliseconds() - 1);
        onChange === null || onChange === void 0 ? void 0 : onChange([s, e]);
        onClose();
    };
    const modifiers = {
        selected: [tempStartDate, tempEndDate].filter((m) => !!m),
    };
    if (tempStartDate && tempEndDate) {
        modifiers.range = {
            from: tempStartDate,
            to: tempEndDate,
        };
    }
    return (React.createElement(DialogContent, { className: "w-[540px] max-w-full max-sm:h-full" },
        React.createElement(DialogTitle, { className: "hidden" }),
        React.createElement("div", { className: "space-y-4 flex-1" },
            React.createElement("h2", { className: "text-2xl" }, "Zeitraum ausw\u00E4hlen"),
            React.createElement("div", { className: "grid grid-cols-2 gap-4" },
                React.createElement("div", { className: "space-y-2" },
                    React.createElement(Label, { htmlFor: "start-date" }, "Von"),
                    React.createElement(Input, { id: "start-date", value: startInputValue, onChange: handleStartInputChange, placeholder: "DD.MM.YYYY" })),
                React.createElement("div", { className: "space-y-2" },
                    React.createElement(Label, { htmlFor: "end-date" }, "Bis"),
                    React.createElement(Input, { id: "end-date", value: endInputValue, onChange: handleEndInputChange, placeholder: startInputValue || "DD.MM.YYYY" }))),
            React.createElement(Calendar, { showWeekNumber: true, mode: "single", selected: tempStartDate, month: month, onSelect: handleSelect, onDayClick: handleCurrentDay, onMonthChange: setMonth, onWeekNumberClick: handleWeekSelection, components: {
                    CaptionLabel,
                }, className: "border rounded-md p-3", modifiers: modifiers, modifiersClassNames: {
                    selected: "bg-primary text-primary-foreground rounded-l-md",
                    range: "bg-accent",
                } })),
        React.createElement(DialogFooter, { className: "gap-2" },
            React.createElement(Button, { variant: "outline", onClick: onClose }, "Abbrechen"),
            React.createElement(Button, { onClick: handleSave, disabled: disabled }, "\u00DCbernehmen"))));
};
// Format the display text for the button
function formatDisplayText(startDate, endDate) {
    if (startDate && endDate) {
        return `${format(startDate, "dd.MM.yyyy")} - ${format(endDate, "dd.MM.yyyy")}`;
    }
    if (startDate) {
        return format(startDate, "dd.MM.yyyy");
    }
    return format(new Date(), "dd.MM.yyyy");
}
export const DateRangePicker = ({ id, value = [], onChange, variant = "outline", title, className, mobileIcon = false, }) => {
    const [startDate, endDate] = value;
    const [open, setOpen] = React.useState(false);
    return (React.createElement(Dialog, { open: open, onOpenChange: setOpen },
        React.createElement(DialogTrigger, { asChild: true },
            React.createElement(Button, { id: id, variant: variant, title: title, className: cn("justify-start text-left font-normal gap-2", className) },
                React.createElement(CalendarIcon, { className: "h-4 w-4" }),
                React.createElement("span", { className: cn(mobileIcon && "max-md:hidden") }, formatDisplayText(startDate, endDate)))),
        open && (React.createElement(PickerDialog, { start: startDate, end: endDate, onClose: () => setOpen(false), onChange: onChange }))));
};
//# sourceMappingURL=DateRangePicker.js.map